import { useEffect, useRef, memo, ReactNode } from 'react';
import { ImageHelper } from '../../../controls/ImageHelper/ImageHelper';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { images } from './../img';
import css from './../index.module.scss';
import classNames from 'classnames';
import { useLocalization } from '../../../context/Localization/localization';
import { formatAsCurrency } from '../../../helpers/Number';
import shave from 'shave';
import { getBasePathName } from '../../../helpers/path';
import { GMUpsellButton } from '../../../controls/ButtonBase';
import type { GoMotoComponent } from '../../../API/GoMoto.types';
import type { DisplayProps } from './Display.types';

export const Display: GoMotoComponent<DisplayProps> = memo(({
    upsell,
    selected,
    toggleService,
}) => {
    const Localization = useLocalization();
    const isKiosk: boolean = isElectron();
    const genericImageSrc: string | undefined = upsell.imagePath ? images[getBasePathName(upsell.imagePath) as keyof typeof images] : undefined;
    const customImageSrc: string | undefined = upsell.localization.imageUrl || undefined;

    const showCustom: boolean = isKiosk && !!customImageSrc;
    const imageSrc: string | undefined = showCustom ? customImageSrc : genericImageSrc;

    const spgPricingText: ReactNode = (
        <SpgPriceText
            price={upsell.price}
            priceLow={upsell.priceLow}
            priceHigh={upsell.priceHigh}
            spgPricing={upsell.spgPricing}
            useSpgPricingLogic={!!upsell.useSpgPricing}
        />
    );

    /**
     * Shave doesn't delimit by anything other than strings and description can have newlines.
     * This prevents lines from being cut off prematurely.
     */
    const description: string | null = upsell.localization.description ? upsell.localization.description.replace(/\n/g, ' \n') : null;
    const titleRef = useRef<HTMLDivElement>(null);
    const descriptionContentRef = useRef<HTMLDivElement>(null);
    const genericDisclaimerRef = useRef<HTMLDivElement>(null);
    const priceTitleRef = useRef<HTMLDivElement>(null);
    const priceDisclaimerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isKiosk) {
            if (titleRef.current) {
                shave(`[class*='${css.title}']`, parseInt(window.getComputedStyle(titleRef.current).height, 10));
            }
            if (descriptionContentRef.current) {
                shave(`[class*='${css.descriptionContent}']`, parseInt(window.getComputedStyle(descriptionContentRef.current).height, 10));
            }
            if (genericDisclaimerRef.current) {
                shave(`[class*='${css.genericDisclaimer}']`, parseInt(window.getComputedStyle(genericDisclaimerRef.current).height, 10));
            }
            if (priceTitleRef.current) {
                shave(`[class*='${css.priceTitle}']`, parseInt(window.getComputedStyle(priceTitleRef.current).height, 10));
            }
            if (priceDisclaimerRef.current) {
                shave(`[class*='${css.priceDisclaimer}']`, parseInt(window.getComputedStyle(priceDisclaimerRef.current).height, 10));
            }
        }
    }, [
        isKiosk,
        upsell.localization.name,
        upsell.localization.description,
        upsell.localization.genericDisclaimer,
        upsell.localization.genericPriceDisclaimer,
        upsell.localization.genericPriceTitle1,
        upsell.localization.genericPriceTitle2,
        upsell.localization.priceDetails,
    ]);

    return (
        <div
            className={showCustom ? css.custom : css.upsellItemGrid}
            style={(isKiosk && {
                backgroundImage: `url(${imageSrc})`,
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
            }) || undefined}
        >
            {imageSrc && !isKiosk && (
                <ImageHelper
                    imageProps={{
                        className: css.img,
                        src: imageSrc,
                        alt: Localization.formatString(Localization.ServiceUpsellImage, upsell.localization.name) as string,
                    }}
                />
            )}
            <div className={css.upsellDetails}>
                {!showCustom && (
                    <div className={css.descriptionWrapper}>
                        <div ref={titleRef} className={classNames(css.title, css['bold'])}>
                            {upsell.localization.name}
                        </div>
                        <div
                            ref={descriptionContentRef}
                            className={classNames(css.descriptionContent, upsell.localization.genericDisclaimer ? css['small'] : css['large'])}
                        >
                            {description}
                        </div>
                        {upsell.localization.genericDisclaimer && (
                            <div ref={genericDisclaimerRef} className={css.genericDisclaimer}>
                                {upsell.localization.genericDisclaimer}
                            </div>
                        )}
                    </div>
                )}
                <div className={css.footer}>
                    {!showCustom ? (
                        <div className={classNames(css.priceWrapper, { [css.horizontal]: !!upsell.genericPrice1 && !!upsell.genericPrice2 })}>
                            <div className={css.prices}>
                                {!!spgPricingText && !!upsell.spgPricing && !!upsell.useSpgPricing && <div className={css.price}>{spgPricingText}</div>}
                                {!showCustom && !upsell.useSpgPricing && (!!upsell.genericPrice1 || !!upsell.genericPrice2) && (
                                    <>
                                        {!!upsell.genericPrice1 && (
                                            <div className={css.left}>
                                                <div ref={priceTitleRef} className={css.priceTitle}>
                                                    {upsell.localization.genericPriceTitle1}{':\r'}
                                                </div>
                                                <div className={css.price}>{formatAsCurrency(upsell.genericPrice1, true)}</div>
                                            </div>
                                        )}
                                        {!!upsell.genericPrice2 && (
                                            <div className={css.right}>
                                                <div className={css.priceTitle}>{upsell.localization.genericPriceTitle2}{':'}</div>
                                                <div className={css.price}>{formatAsCurrency(upsell.genericPrice2, true)}</div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {!showCustom && !upsell.useSpgPricing && (!!upsell.genericPrice1 || !!upsell.genericPrice2) && (
                                <div ref={priceDisclaimerRef} className={css.priceDisclaimer}>
                                    {upsell.localization.genericPriceDisclaimer}
                                </div>
                            )}
                        </div>
                    ) : null}
                    <GMUpsellButton
                        className={css.button}
                        title={selected ? undefined : Localization.ClickToAddThisService}
                        label={selected ? Localization.RemoveService : Localization.AddService}
                        styleType={selected ? 'primary' : 'secondary'}
                        onClick={() => toggleService(upsell)}
                        add={!selected}
                        id={selected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                        ariaLabel={selected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                    />
                </div>
            </div>
        </div>
    );
});
